import type React from "react"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Bot } from "lucide-react"

interface FloatingAgentButtonProps {
  onClick: () => void
  isOpen: boolean
  label?: string
}

const DRAG_THRESHOLD = 6 // pixels
const VIEWPORT_PADDING = 16 // pixels
const BUTTON_SIZE = 64 // pixels

// Clamp position to keep button inside viewport
function clampPosition(x: number, y: number) {
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    x = window.innerWidth - BUTTON_SIZE - VIEWPORT_PADDING
    y = window.innerHeight - BUTTON_SIZE - VIEWPORT_PADDING
  }
  const maxX = window.innerWidth - BUTTON_SIZE - VIEWPORT_PADDING
  const maxY = window.innerHeight - BUTTON_SIZE - VIEWPORT_PADDING

  return {
    x: Math.max(VIEWPORT_PADDING, Math.min(x, maxX)),
    y: Math.max(VIEWPORT_PADDING, Math.min(y, maxY)),
  }
}

export function FloatingAgentButton({ onClick, isOpen, label = "Open HEXA Agent" }: FloatingAgentButtonProps) {
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dragStartRef = useRef({ x: 0, y: 0, moved: 0 })

  // Load position from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem("hexa-agent-position")
    let initialPosition = { x: window.innerWidth - 88, y: window.innerHeight - 88 }

    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        if (parsed && Number.isFinite(parsed.x) && Number.isFinite(parsed.y)) initialPosition = parsed
      } catch {
        // Use default position
      }
    }

    // Always clamp to ensure position is inside viewport
    setPosition(clampPosition(initialPosition.x, initialPosition.y))
  }, [])

  // Re-clamp position on window resize
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev.x, prev.y))
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault()
    setIsDragging(true)
    dragStartRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
      moved: 0,
    }

    if (buttonRef.current) {
      buttonRef.current.setPointerCapture(e.pointerId)
    }
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return

    const newX = e.clientX - dragStartRef.current.x
    const newY = e.clientY - dragStartRef.current.y

    // Track total movement
    const dx = newX - position.x
    const dy = newY - position.y
    dragStartRef.current.moved += Math.sqrt(dx * dx + dy * dy)

    const clamped = clampPosition(newX, newY)
    setPosition(clamped)
  }

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!isDragging) return

    setIsDragging(false)

    // Save position to localStorage
    localStorage.setItem("hexa-agent-position", JSON.stringify(position))

    if (buttonRef.current) {
      buttonRef.current.releasePointerCapture(e.pointerId)
    }

    // The click handler also supports keyboard activation; ignore clicks after a drag.
  }

  if (isOpen) return null

  return createPortal(
    <button
      ref={buttonRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => { setIsDragging(false); dragStartRef.current.moved = DRAG_THRESHOLD }}
      onClick={() => {
        if (dragStartRef.current.moved < DRAG_THRESHOLD) onClick()
        dragStartRef.current.moved = 0
      }}
      style={{
        position: "fixed",
        left: `${position.x}px`,
        top: `${position.y}px`,
        touchAction: "none",
        cursor: isDragging ? "grabbing" : "grab",
        boxShadow: "0 4px 16px rgba(106,77,255,0.3)",
      }}
      className="w-16 h-16 rounded-full transition-shadow z-50 ring-2 ring-[var(--hexa-border)]"
      aria-label={label}
    >
      {/* Agent icon - replace with actual asset */}
      <div className="relative flex items-center justify-center w-full h-full rounded-full overflow-hidden bg-[var(--hexa-purple)] text-white">
        <Bot className="h-8 w-8" aria-hidden="true" />
        <img
          src="/images/hexa-agent.png"
          alt="HEXA Agent"
          width={64}
          height={64}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Glow effect */}
        <div
          className="absolute inset-0 rounded-full opacity-50"
          style={{
            background: "radial-gradient(circle, var(--hexa-purple-glow, rgba(123, 63, 255, 0.3)) 0%, transparent 70%)",
          }}
        />
      </div>
    </button>,
    document.body
  )
}
